/**
 * `useConfiguratorNavigation` — wizard navigation hook for the step-by-step
 * product configurator.
 *
 * Encapsulates dispatch calls for the three navigation actions (`advance`,
 * `goBack`, `jumpToStep`) and exposes `resetConfigurator` for the wizard
 * page unmount. All returned functions are stable references (wrapped in
 * `useCallback`) so memoised step components don't re-render when
 * unrelated slice fields change.
 *
 * Derived booleans:
 *   - `isFirstStep` — `currentStep === 0`
 *   - `isFinalStep` — `currentStep === stepSequence.length - 1`
 *
 * The `fetchConfiguratorConfig` thunk is NOT dispatched from this hook;
 * that's the configurator page component's responsibility (SF-CONFIG-004).
 *
 * Per `.claude/rules/frontend-data-flow.md §1`: hooks in
 * `@dfq/shared/hooks/` may use Redux, but only dispatch synchronous actions.
 * Network calls stay in the view layer.
 *
 * SF-CONFIG-001.
 */
import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@dfq/shared/stores/hooks';
import {
  advance,
  goBack,
  jumpToStep,
  resetConfigurator,
  selectCompletedSteps,
  selectConfig,
  selectCurrentStep,
} from '@dfq/shared/stores/configurator';

export interface UseConfiguratorNavigationResult {
  currentStep: number;
  completedSteps: number[];
  isFirstStep: boolean;
  isFinalStep: boolean;
  advance: () => void;
  goBack: () => void;
  jumpToStep: (step: number) => void;
  resetConfigurator: () => void;
}

/**
 * Returns navigation state and stable dispatch wrappers for the wizard.
 */
export function useConfiguratorNavigation(): UseConfiguratorNavigationResult {
  const dispatch = useAppDispatch();
  const currentStep = useAppSelector(selectCurrentStep);
  const completedSteps = useAppSelector(selectCompletedSteps);
  const config = useAppSelector(selectConfig);

  const totalSteps = config?.stepSequence.length ?? 0;
  const isFirstStep = currentStep === 0;
  const isFinalStep = totalSteps > 0 && currentStep === totalSteps - 1;

  const handleAdvance = useCallback((): void => {
    dispatch(advance());
  }, [dispatch]);

  const handleGoBack = useCallback((): void => {
    dispatch(goBack());
  }, [dispatch]);

  const handleJumpToStep = useCallback(
    (step: number): void => {
      dispatch(jumpToStep(step));
    },
    [dispatch],
  );

  const handleReset = useCallback((): void => {
    dispatch(resetConfigurator());
  }, [dispatch]);

  return {
    currentStep,
    completedSteps,
    isFirstStep,
    isFinalStep,
    advance: handleAdvance,
    goBack: handleGoBack,
    jumpToStep: handleJumpToStep,
    resetConfigurator: handleReset,
  };
}
